import React, { useEffect, useState } from 'react';
import apiDefinitions from '../../../core/api/apiDefinitions';
import { showError } from '../../../core/alert/sweetAlertConfig';
import { Table, Button } from 'react-bootstrap';

interface Handover {
  id: number;
  letterIds: number[];
  handoverTo: string;
  handoverDateTime: string;
  department: string | null;
}

interface LetterDTO {
  id: number;
  date: string;
  referenceNo: string;
  letterType: string;
  senderDetails: string;
  letterDescription: string;
  status: string;
  handovers: Handover[];
}

interface ViewLetterDetailsProps {
  show: boolean;
  onHide: () => void;
  letterId: number;
}

const ViewLetterDetails: React.FC<ViewLetterDetailsProps> = ({ show, onHide, letterId }) => {
  const [letter, setLetter] = useState<LetterDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show && letterId) {
      setIsLoading(true);
      apiDefinitions
        .getLetterById(letterId)
        .then((res) => {
          if (res.status === 200 && res.data.status === 'S1000') {
            setLetter(res.data.data);
          } else {
            showError(res.data?.statusDescription || 'Failed to fetch letter details');
          }
        })
        .catch((e) => {
          showError('Failed to fetch letter details. Please try again.');
        })
        .finally(() => setIsLoading(false));
    }
  }, [show, letterId]);

  return (
    <div className={`modal custom-modal fade ${show ? 'show' : ''}`} id="viewLetterModal" role="dialog" style={{ display: show ? 'block' : 'none' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Letter Details</h4>
            <button className="btn-close" onClick={onHide} aria-label="Close">
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="modal-body">
            {isLoading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : letter ? (
              <div>
                {/* Letter Details Table */}
                <div className="card mb-4">
                  <div className="card-body">
                    <h5 className="mb-3">Letter Information</h5>
                    <div className="table-responsive">
                      <Table className="table text-nowrap table-borderless">
                        <tbody>
                          <tr>
                            <th scope="row">ID</th>
                            <td>{letter.id}</td>
                          </tr>
                          <tr>
                            <th scope="row">Date</th>
                            <td>{new Date(letter.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                          </tr>
                          <tr>
                            <th scope="row">Reference No</th>
                            <td>{letter.referenceNo}</td>
                          </tr>
                          <tr>
                            <th scope="row">Letter Type</th>
                            <td>{letter.letterType}</td>
                          </tr>
                          <tr>
                            <th scope="row">Sender Details</th>
                            <td>{letter.senderDetails}</td>
                          </tr>
                          <tr>
                            <th scope="row">Description</th>
                            <td>{letter.letterDescription || '-'}</td>
                          </tr>
                          <tr>
                            <th scope="row">Status</th>
                            <td>
                              <span className={`badge bg-${letter.status === 'RECEIVED' ? 'success' : 'info'}`}>
                                {letter.status}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>

                {/* Handovers Table */}
                <div className="card">
                  <div className="card-body">
                    <h5 className="mb-3">Handover Details</h5>
                    {letter.handovers.length > 0 ? (
                      <div className="table-responsive">
                        <Table className="table text-nowrap table-borderless">
                          <thead>
                            <tr>
                              <th scope="col">Handover ID</th>
                              <th scope="col">Handover To (Emp No)</th>
                              <th scope="col">Handover Date</th>
                              <th scope="col">Department</th>
                            </tr>
                          </thead>
                          <tbody>
                            {letter.handovers.map((handover) => (
                              <tr key={handover.id}>
                                <td>{handover.id}</td>
                                <td>{handover.handoverTo}</td>
                                <td>
                                  {new Date(handover.handoverDateTime).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </td>
                                <td>{handover.department || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <p>No handovers recorded.</p>
                    )}
                  </div>
                </div>

                <div className="modal-btn text-end mt-3">
                  <Button variant="primary" onClick={onHide}>
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <p>No details available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewLetterDetails;